import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import type { RootState } from "../../redux/store";
import { createUser } from "../../services/usersList.service";
import { searchDoctors, type DoctorSearchResult } from "../../services/doctors.service";

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
}

const ROLE_OPTIONS = [
  "USER",
  "SECRETARY",
  "FINANCIAL",
  "DOCTOR",
  "OWNER",
  "DOCTOR_OWNER",
] as const;
type RoleOption = (typeof ROLE_OPTIONS)[number];

// Mapeia o código da role (backend) para um rótulo legível em português
const getRoleLabel = (role: RoleOption) => {
  switch (role) {
    case "USER":
      return "Usuário";
    case "SECRETARY":
      return "Secretaria";
    case "FINANCIAL":
      return "Financeiro";
    case "DOCTOR":
      return "Médico";
    case "OWNER":
      return "Proprietário";
    case "DOCTOR_OWNER":
      return "Médico proprietário";
    default:
      return role;
  }
};

export function CreateUserModal({ open, onClose }: CreateUserModalProps) {
  const { user: currentUser } = useSelector((state: RootState) => state.userInfo);
  const currentUserRole = String(currentUser?.role || "").toUpperCase();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState<RoleOption>("USER");
  const [loading, setLoading] = useState(false);

  // Campos adicionais de perfil (UserProfileDto) – usados quando role === 'USER'
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateUF, setStateUF] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [birthDay, setBirthDay] = useState(""); // formato yyyy-mm-dd
  const [registrationNumber, setRegistrationNumber] = useState(""); // CPF
  const [phone, setPhone] = useState("");
  const [socialProgram, setSocialProgram] = useState<boolean | null>(null);
  const [programName, setProgramName] = useState("");

  // SECRETARY
  const [secretaryDepartment, setSecretaryDepartment] = useState("");
  const [secretaryPhone, setSecretaryPhone] = useState("");

  // SECRETARY - médicos vinculados
  const [linkedDoctors, setLinkedDoctors] = useState<DoctorSearchResult[]>([]);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState("");
  const [doctorSearchResults, setDoctorSearchResults] = useState<DoctorSearchResult[]>([]);
  const [doctorSearchLoading, setDoctorSearchLoading] = useState(false);

  // FINANCIAL
  const [financialDepartment, setFinancialDepartment] = useState("");
  const [financialPhone, setFinancialPhone] = useState("");

  // DOCTOR
  const [doctorCrm, setDoctorCrm] = useState("");
  const [doctorSpecialty, setDoctorSpecialty] = useState("");
  const [doctorPhone, setDoctorPhone] = useState("");

  type DoctorLocalForm = {
    localName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
  };

  const emptyDoctorLocal: DoctorLocalForm = {
    localName: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
  };

  const [doctorLocals, setDoctorLocals] = useState<DoctorLocalForm[]>([emptyDoctorLocal]);

  // Regras de quais roles podem ser criadas por cada tipo de usuário logado
  const isAdmin = currentUserRole === "ADMIN";
  const isOwner = currentUserRole === "OWNER";
  const isDoctorOwner = currentUserRole === "DOCTOR_OWNER";

  const allowedRoleOptions: RoleOption[] = ROLE_OPTIONS.filter((targetRole) => {
    // Sempre permitido (para quem já passou pela verificação do backend):
    if (["USER", "SECRETARY", "FINANCIAL", "DOCTOR"].includes(targetRole)) {
      return isAdmin || isOwner || isDoctorOwner;
    }

    // OWNER e DOCTOR_OWNER só podem ser criados por ADMIN
    if (["OWNER", "DOCTOR_OWNER"].includes(targetRole)) {
      return isAdmin;
    }

    return false;
  });

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setRole("USER");
    setAddress("");
    setCity("");
    setStateUF("");
    setZipCode("");
    setBirthDay("");
    setRegistrationNumber("");
    setPhone("");
    setSocialProgram(null);
    setProgramName("");
    setSecretaryDepartment("");
    setSecretaryPhone("");
    setLinkedDoctors([]);
    setDoctorSearchTerm("");
    setDoctorSearchResults([]);
    setFinancialDepartment("");
    setFinancialPhone("");
    setDoctorCrm("");
    setDoctorSpecialty("");
    setDoctorPhone("");
    setDoctorLocals([emptyDoctorLocal]);
  };

  const handleSearchDoctors = async () => {
    const term = doctorSearchTerm.trim();
    if (!term) {
      toast.error("Digite CPF, CRM ou nome para buscar o médico.");
      return;
    }

    setDoctorSearchLoading(true);
    try {
      const results = await searchDoctors(term);
      setDoctorSearchResults(results);
    } catch (error: any) {
      console.error(error);
      const status = error?.response?.status;
      if (status === 403) {
        toast.error("Você não tem permissão para buscar médicos. Apenas ADMIN, OWNER ou DOCTOR_OWNER podem acessar esta função.");
      } else {
        toast.error("Não foi possível buscar médicos. Tente novamente.");
      }
    } finally {
      setDoctorSearchLoading(false);
    }
  };

  const handleAddDoctorToSecretary = (doctor: DoctorSearchResult) => {
    if (linkedDoctors.some((d) => d.doctorId === doctor.doctorId)) {
      return;
    }
    setLinkedDoctors((prev) => [...prev, doctor]);
  };

  const handleRemoveLinkedDoctor = (doctorId: string) => {
    setLinkedDoctors((prev) => prev.filter((d) => d.doctorId !== doctorId));
  };

  const handleSave = async () => {
    if (!name.trim() || !email.trim() || !password.trim() || !confirmPassword.trim()) {
      toast.error("Preencha nome, e-mail e senha.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("A confirmação de senha não confere.");
      return;
    }

    // Validação específica para perfil social (USER com programa social)
    if (role === "USER" && socialProgram === true) {
      if (!registrationNumber.trim() || !birthDay.trim()) {
        toast.error("Para CadÚnico, CPF e data de nascimento são obrigatórios.");
        return;
      }
    }

    setLoading(true);
    try {
      // Backend protegido (/user/create-with-role) cria usuários com role específica,
      // respeitando as regras de autorização (ADMIN, OWNER, DOCTOR_OWNER) e de roles alvo
      // (não permite ADMIN, e OWNER/DOCTOR_OWNER apenas quando o criador é ADMIN).
      await createUser({
        name: name.trim(),
        email: email.trim(),
        password: password.trim(),
        confirmPassword: confirmPassword.trim(),
        role,
        // OBS: os campos de perfil (address, city, etc.) fazem parte do UserProfileDto
        // e hoje são persistidos via endpoint /user/profile quando o próprio usuário
        // estiver autenticado. Aqui só fazemos a coleta conforme o tipo de perfil.
        profileDraft:
          role === "USER"
            ? {
                address: address || undefined,
                city: city || undefined,
                state: stateUF || undefined,
                zip_code: zipCode || undefined,
                birthDay: birthDay || undefined,
                registrationNumber: registrationNumber || undefined,
                phone: phone || undefined,
                socialProgram: socialProgram ?? undefined,
                programName: programName || undefined,
              }
            : undefined,
        roleData:
          role === "SECRETARY"
            ? {
                department: secretaryDepartment || undefined,
                phone: secretaryPhone || undefined,
                // IDs (doctorId) dos médicos vinculados a esta secretaria
                doctorIds:
                  linkedDoctors.length > 0
                    ? linkedDoctors.map((d) => d.doctorId)
                    : undefined,
              }
            : role === "FINANCIAL"
            ? {
                department: financialDepartment || undefined,
                phone: financialPhone || undefined,
              }
            : role === "DOCTOR"
            ? {
                crm: doctorCrm || undefined,
                specialty: doctorSpecialty || undefined,
                phone: doctorPhone || undefined,
                // mantém compatibilidade com backend atual enviando o primeiro local também em "local"
                local:
                  doctorLocals && doctorLocals.length > 0
                    ? {
                        localName: doctorLocals[0].localName || undefined,
                        address: doctorLocals[0].address || undefined,
                        city: doctorLocals[0].city || undefined,
                        state: doctorLocals[0].state || undefined,
                        zip_code: doctorLocals[0].zip || undefined,
                        phone: doctorLocals[0].phone || undefined,
                      }
                    : undefined,
                // novo formato com múltiplos locais
                locals: doctorLocals
                  .filter((loc) =>
                    [loc.localName, loc.address, loc.city, loc.state, loc.zip, loc.phone]
                      .some((v) => v && v.trim() !== "")
                  )
                  .map((loc) => ({
                    localName: loc.localName || undefined,
                    address: loc.address || undefined,
                    city: loc.city || undefined,
                    state: loc.state || undefined,
                    zip_code: loc.zip || undefined,
                    phone: loc.phone || undefined,
                  })),
              }
            : undefined,
      });

      toast.success("Usuário criado com sucesso!");
      resetForm();
      onClose();
    } catch (error) {
      // createUser já exibe toast de erro; apenas mantém fallback
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showUserProfileFields = role === "USER";
  const showSocialProgramFields = showUserProfileFields && socialProgram === true;
  const showSecretaryFields = role === "SECRETARY";
  const showFinancialFields = role === "FINANCIAL";
  const showDoctorFields = role === "DOCTOR";

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Criar novo usuário</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Informe os dados do usuário, escolha o tipo de perfil 
          </Typography>

          <TextField
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />

          <TextField
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Senha"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            <TextField
              label="Confirmar senha"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
            />
          </Stack>

          <FormControl fullWidth>
            <InputLabel id="role-select-label">Tipo de perfil </InputLabel>
            <Select
              labelId="role-select-label"
              label="Tipo de perfil"
              value={role}
              onChange={(e) => setRole(e.target.value as RoleOption)}
            >
              {allowedRoleOptions.map((r) => (
                <MenuItem key={r} value={r}>
                  {getRoleLabel(r)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
            </FormHelperText>
          </FormControl>

          {showUserProfileFields && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Informações de perfil
              </Typography>

              <TextField
                label="Endereço completo"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Cidade"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Estado"
                  value={stateUF}
                  onChange={(e) => setStateUF(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="CEP"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Telefone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Data de nascimento"
                  type="date"
                  value={birthDay}
                  onChange={(e) => setBirthDay(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={socialProgram === true}
                    indeterminate={socialProgram === null}
                    onChange={(e) =>
                      setSocialProgram(e.target.checked ? true : false)
                    }
                  />
                }
                label="Usuário inscrito em programa social (CadÚnico)"
              />

              {showSocialProgramFields && (
                <Stack spacing={2}>
                  <TextField
                    label="CPF (para validação no CadÚnico)"
                    value={registrationNumber}
                    onChange={(e) => setRegistrationNumber(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Nome do programa (opcional)"
                    value={programName}
                    onChange={(e) => setProgramName(e.target.value)}
                    fullWidth
                  />
                </Stack>
              )}
            </Stack>
          )}

          {showSecretaryFields && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Informações de perfil (Secretaria)
              </Typography>
              <TextField
                label="Departamento / Setor"
                value={secretaryDepartment}
                onChange={(e) => setSecretaryDepartment(e.target.value)}
                fullWidth
              />
              <TextField
                label="Telefone de contato"
                value={secretaryPhone}
                onChange={(e) => setSecretaryPhone(e.target.value)}
                fullWidth
              />

              <Stack spacing={1.5}>
                <Typography variant="subtitle2" color="text.secondary">
                  Vincular médicos à secretaria
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Buscar médico por CPF, CRM ou nome"
                    value={doctorSearchTerm}
                    onChange={(e) => setDoctorSearchTerm(e.target.value)}
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    onClick={handleSearchDoctors}
                    disabled={doctorSearchLoading}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {doctorSearchLoading ? "Buscando..." : "Buscar"}
                  </Button>
                </Stack>

                {doctorSearchResults.length > 0 && (
                  <Stack spacing={1} sx={{ mt: 1, maxHeight: 200, overflowY: "auto" }}>
                    {doctorSearchResults.map((doctor) => {
                      const alreadyLinked = linkedDoctors.some((d) => d.doctorId === doctor.doctorId);
                      return (
                        <Stack
                          key={doctor.doctorId}
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          justifyContent="space-between"
                          sx={{ border: "1px solid #eee", borderRadius: 1, p: 1 }}
                        >
                          <Stack spacing={0.5}>
                            <Typography variant="body2" fontWeight={500}>
                              {doctor.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {doctor.crm ? `CRM: ${doctor.crm}` : "CRM não informado"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {doctor.cpf ? `CPF: ${doctor.cpf}` : "CPF não informado"}
                            </Typography>
                          </Stack>
                          <Button
                            variant="text"
                            onClick={() => handleAddDoctorToSecretary(doctor)}
                            disabled={alreadyLinked}
                          >
                            {alreadyLinked ? "Já vinculado" : "Vincular"}
                          </Button>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}

                {linkedDoctors.length > 0 && (
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Médicos já vinculados
                    </Typography>
                    {linkedDoctors.map((doctor) => (
                      <Stack
                        key={doctor.doctorId}
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                        sx={{ border: "1px solid #eee", borderRadius: 1, p: 1 }}
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={500}>
                            {doctor.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doctor.crm ? `CRM: ${doctor.crm}` : "CRM não informado"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doctor.cpf ? `CPF: ${doctor.cpf}` : "CPF não informado"}
                          </Typography>
                        </Stack>
                          <Button
                          variant="text"
                          color="error"
                          onClick={() => handleRemoveLinkedDoctor(doctor.doctorId)}
                        >
                          Remover vínculo
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          )}

          {showFinancialFields && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Informações de perfil (Financeiro)
              </Typography>
              <TextField
                label="Departamento / Setor"
                value={financialDepartment}
                onChange={(e) => setFinancialDepartment(e.target.value)}
                fullWidth
              />
              <TextField
                label="Telefone de contato"
                value={financialPhone}
                onChange={(e) => setFinancialPhone(e.target.value)}
                fullWidth
              />
            </Stack>
          )}

          {showDoctorFields && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Informações de perfil (Médico)
              </Typography>
              <TextField
                label="CRM"
                value={doctorCrm}
                onChange={(e) => setDoctorCrm(e.target.value)}
                fullWidth
              />
              <TextField
                label="Especialidade"
                value={doctorSpecialty}
                onChange={(e) => setDoctorSpecialty(e.target.value)}
                fullWidth
              />
              <TextField
                label="Telefone profissional"
                value={doctorPhone}
                onChange={(e) => setDoctorPhone(e.target.value)}
                fullWidth
              />

              <Typography variant="subtitle2" color="text.secondary">
                Locais de atendimento (Locals)
              </Typography>

              {doctorLocals.map((local, index) => (
                <Stack key={index} spacing={1.5} sx={{ p: 1, borderRadius: 1, border: "1px solid #eee" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {doctorLocals.length > 1 ? `Local ${index + 1}` : "Local"}
                  </Typography>
                  <TextField
                    label="Nome do local (clínica, consultório, etc.)"
                    value={local.localName}
                    onChange={(e) => {
                      const next = [...doctorLocals];
                      next[index] = { ...next[index], localName: e.target.value };
                      setDoctorLocals(next);
                    }}
                    fullWidth
                  />
                  <TextField
                    label="Endereço do local"
                    value={local.address}
                    onChange={(e) => {
                      const next = [...doctorLocals];
                      next[index] = { ...next[index], address: e.target.value };
                      setDoctorLocals(next);
                    }}
                    fullWidth
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Cidade"
                      value={local.city}
                      onChange={(e) => {
                        const next = [...doctorLocals];
                        next[index] = { ...next[index], city: e.target.value };
                        setDoctorLocals(next);
                      }}
                      fullWidth
                    />
                    <TextField
                      label="Estado"
                      value={local.state}
                      onChange={(e) => {
                        const next = [...doctorLocals];
                        next[index] = { ...next[index], state: e.target.value };
                        setDoctorLocals(next);
                      }}
                      fullWidth
                    />
                    <TextField
                      label="CEP"
                      value={local.zip}
                      onChange={(e) => {
                        const next = [...doctorLocals];
                        next[index] = { ...next[index], zip: e.target.value };
                        setDoctorLocals(next);
                      }}
                      fullWidth
                    />
                  </Stack>
                  <TextField
                    label="Telefone do local"
                    value={local.phone}
                    onChange={(e) => {
                      const next = [...doctorLocals];
                      next[index] = { ...next[index], phone: e.target.value };
                      setDoctorLocals(next);
                    }}
                    fullWidth
                  />

                  {doctorLocals.length > 1 && (
                    <Button
                      variant="text"
                      color="error"
                      onClick={() => {
                        setDoctorLocals((prev) => prev.filter((_, i) => i !== index));
                      }}
                      sx={{ alignSelf: "flex-end" }}
                    >
                      Remover local
                    </Button>
                  )}
                </Stack>
              ))}

              <Button
                variant="outlined"
                onClick={() => setDoctorLocals((prev) => [...prev, emptyDoctorLocal])}
                sx={{ alignSelf: "flex-start" }}
              >
                Adicionar local
              </Button>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? "Salvando..." : "Criar usuário"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
